/**
 * 알림 스위트 라벨 SSoT (Single Source of Truth).
 *
 * 내부 enum/식별자를 화면에 절대 노출하지 않기 위한 라벨 매핑 정본이다.
 * 모든 매핑은 i18n `notifications` 네임스페이스의 키를 가리키며, 누락(미매핑) 시
 * `'-'` 로 폴백한다(raw enum 노출 방지). 컴포넌트는 이 헬퍼만 사용하고 raw 값을
 * 직접 렌더하지 않는다.
 */
import type { TFunction } from 'i18next';
import type { NotificationChannel } from '../types/notificationMessage';

/** 라벨 헬퍼가 받는 t 함수 — `notifications` 네임스페이스 TFunction(코드베이스 표준 패턴). */
export type TFn = TFunction<'notifications', undefined>;

/** 미매핑 라벨 폴백 — raw enum/식별자 노출 방지. */
export const LABEL_FALLBACK = '-';

/** i18n 라벨 조회 — 키가 없으면 '-'(raw 노출 금지). */
const lookup = (t: TFn, key: string): string => {
  const value = t(key, LABEL_FALLBACK);
  return value && value !== key ? value : LABEL_FALLBACK;
};

/** 채널(EMAIL/SMS/KAKAO/KAKAO_BIZ) → 한글 라벨. */
export const channelLabel = (
  t: TFn,
  channel: NotificationChannel | string | null | undefined,
): string => (channel ? lookup(t, `channel.${channel}`) : LABEL_FALLBACK);

/** 발송 채널 옵션(템플릿/발송에서 선택 가능한 3채널). KAKAO(소셜)는 제외. */
export const SENDABLE_CHANNELS: ReadonlyArray<NotificationChannel> = [
  'EMAIL',
  'SMS',
  'KAKAO_BIZ',
];

/** 메시지 발송 상태 → 한글 라벨. */
export const messageStatusLabel = (
  t: TFn,
  status: string | null | undefined,
): string => (status ? lookup(t, `messageStatus.${status}`) : LABEL_FALLBACK);

/** 대량/내역 job 상태 → 한글 라벨. */
export const jobStatusLabel = (
  t: TFn,
  status: string | null | undefined,
): string => (status ? lookup(t, `jobStatus.${status}`) : LABEL_FALLBACK);

export type StatusTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/** 발송 상태 → StatusBadge variant. 미지정 상태는 neutral. */
export const messageStatusTone = (status: string | null | undefined): StatusTone => {
  switch (status) {
    case 'SENT':
    case 'DELIVERED':
    case 'COMPLETED':
      return 'success';
    case 'QUEUED':
    case 'SENDING':
    case 'RUNNING':
    case 'PENDING':
      return 'info';
    case 'FAILED_TRANSIENT':
    // notification #28 — 알림톡 실패 후 문자로 대신 보냄(성공도 실패도 아님). 최종 결과는 대체 문자 건이 보여 준다.
    case 'FALLBACK':
      return 'warning';
    case 'FAILED':
    case 'FAILED_PERMANENT':
      return 'danger';
    case 'SUPPRESSED':
      return 'neutral';
    default:
      return 'neutral';
  }
};

/** 언어 코드(KO_KR 등) → 한글 라벨. */
export const languageLabel = (
  t: TFn,
  code: string | null | undefined,
): string => (code ? lookup(t, `language.${code}`) : LABEL_FALLBACK);

/**
 * 언어(로케일) 코드 → 몰 라벨(KO_KR=한국몰/JA_JP=일문몰/ZH_CN=중문몰/EN_US=영문몰).
 * 수신자 국적→로케일 자동매칭 발송에서 "어느 몰로 나가는지"를 표기한다(collectionMalls 정합).
 * 미매핑 코드는 언어 라벨로 폴백(raw 노출 방지).
 */
export const mallLabel = (
  t: TFn,
  code: string | null | undefined,
): string => {
  if (!code) return LABEL_FALLBACK;
  const value = t(`mall.${code}`, LABEL_FALLBACK);
  return value && value !== `mall.${code}` && value !== LABEL_FALLBACK
    ? value
    : languageLabel(t, code);
};

/**
 * 트리거 코드(=templateCode) → 한글 라벨 폴백.
 *
 * ★ 라벨 SSoT 1순위는 BE 트리거 카탈로그(`catalog.label`)다 — 호출부(AutoNotificationTab)는
 *   `catalog.label ?? triggerLabel(t, code) ?? code` 순으로 폴백한다. 이 헬퍼는 카탈로그가
 *   라벨을 주지 못한 트리거(미연동/배포 시차)에 raw 코드가 노출되지 않도록 i18n `trigger.<CODE>`
 *   를 조회한다. 키가 없으면 null 을 반환해 호출부가 다음 폴백(raw code)으로 넘어가게 한다.
 *   (ID-226 신규 8 트리거 라벨을 4로케일로 시드해 글로벌몰에서도 안전.)
 */
export const triggerLabel = (
  t: TFn,
  code: string | null | undefined,
): string | null => {
  if (!code) return null;
  const value = t(`trigger.${code}`, LABEL_FALLBACK);
  return value && value !== `trigger.${code}` && value !== LABEL_FALLBACK ? value : null;
};

/**
 * 발송 내역 실패 사유가 수신자 허용 목록(테스트 모드) 차단인지. BE 는 providerCode ALLOWLIST_BLOCKED 와 함께
 * 사유 "허용 목록에 없는 수신자 — 발송 차단 (수신자 허용 목록 모드)" 를 남기고, 수신자 목록 API 는 사유만 준다.
 */
export const isAllowlistBlocked = (errorMessage: string | null | undefined): boolean =>
  !!errorMessage && (errorMessage.includes('ALLOWLIST_BLOCKED') || errorMessage.includes('허용 목록'));
